import { useEffect, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { useTranslation } from 'react-i18next'
import { useCart } from './cart/CartContext'
import { useAuth } from './account/AuthContext'
import { useShop, placeOrder } from '../shared/api/storefront'
import { useAddresses, createAddress } from '../shared/api/account'
import { errorMessage } from '../shared/api/errorMessage'
import { isPhone, isPersonName } from '../shared/validation'
import { formatMoney } from '../shared/money'
import type { FulfillmentType } from '../shared/types'

/**
 * Login is compulsory to order: a visitor who isn't logged in is sent to /login, which brings
 * them back here afterwards (the cart survives the trip, see CartContext). The server refuses an
 * order without a customer token too.
 */
export default function CheckoutPage() {
  const { isAuthenticated, initializing } = useAuth()
  const location = useLocation()
  if (initializing) {
    // The boot silent refresh is restoring the session (D-053), don't bounce to login mid-flight.
    return (
      <Container maxWidth="sm" sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    )
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />
  }
  // Mounted only once logged in, so the form's name/phone prefill sees the session.
  return <CheckoutForm />
}

/**
 * Checkout form: name/phone/address (+ notes) → COD order, prefilled from the customer's session
 * and saved addresses; the order links to their account (the bearer token is attached
 * automatically). The server prices the order.
 */
function CheckoutForm() {
  const { t } = useTranslation()
  const { data: shop } = useShop()
  const currency = shop?.currency ?? 'INR'
  const showPrices = shop?.showPrices ?? true // priceless shop (D-069): no money on checkout
  const { items, subtotal, clear } = useCart()
  const { session } = useAuth()
  const { data: addresses } = useAddresses(true)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const [fulfillment, setFulfillment] = useState<FulfillmentType>('DELIVERY')
  const [name, setName] = useState(session?.name ?? '')
  const [phone, setPhone] = useState(session?.phone ?? '')
  const [address, setAddress] = useState('')
  const [notes, setNotes] = useState('')
  // Saved-address UX (logged-in + delivery): 'new' = type a fresh address, else a saved address id.
  const [selectedAddressId, setSelectedAddressId] = useState('new')
  const [saveToAccount, setSaveToAccount] = useState(false)
  const [label, setLabel] = useState('')
  const [touched, setTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Preselect the customer's first saved address so a returning customer checks out without
  // re-typing. One-shot (ref-guarded): fills the form once the addresses load, but never stomps a
  // later manual choice — including switching back to "new address".
  const addressInited = useRef(false)
  useEffect(() => {
    if (addressInited.current || !addresses || addresses.length === 0) {
      return
    }
    addressInited.current = true
    const first = addresses[0]
    setSelectedAddressId(first.id)
    setAddress(first.address)
    setPhone(first.phone)
  }, [addresses])

  // Empty cart (e.g. direct nav or after clearing) — nothing to check out.
  if (items.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="info" action={<Button onClick={() => navigate('/')}>{t('checkout.browse')}</Button>}>
          {t('cart.empty')}
        </Alert>
      </Container>
    )
  }

  const isDelivery = fulfillment === 'DELIVERY'
  const hasSaved = !!addresses && addresses.length > 0
  const isNewAddress = selectedAddressId === 'new'
  const canSaveAddress = isDelivery && isNewAddress
  // Blank → "required"; present-but-malformed → the format message. `invalid` blocks submit.
  const nameError = !name.trim() ? t('common.required') : !isPersonName(name) ? t('validation.name') : ''
  const phoneError = !phone.trim() ? t('common.required') : !isPhone(phone) ? t('validation.phone') : ''
  const addressError = isDelivery && !address.trim()
  const labelError = canSaveAddress && saveToAccount && !label.trim()
  const invalid = nameError || phoneError || addressError || labelError

  // Dropdown change: fill from a saved address, or clear the field to type a new one.
  const onSelectAddress = (value: string) => {
    setSelectedAddressId(value)
    if (value === 'new') {
      setAddress('')
    } else {
      const a = addresses?.find((x) => x.id === value)
      if (a) {
        setAddress(a.address)
        setPhone(a.phone)
      }
    }
  }

  const handleSubmit = async () => {
    setTouched(true)
    if (invalid) {
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const order = await placeOrder({
        customerName: name.trim(),
        customerPhone: phone.trim(),
        fulfillmentType: fulfillment,
        deliveryAddress: isDelivery ? address.trim() : undefined,
        notes: notes.trim() || undefined,
        items: items.map((i) => ({ productId: i.product.id, quantity: i.quantity, selections: i.selections })),
      })
      // Best-effort: the order is already placed; never block the confirmation on an address save.
      if (canSaveAddress && saveToAccount) {
        try {
          await createAddress({ label: label.trim(), address: address.trim(), phone: phone.trim() })
          queryClient.invalidateQueries({ queryKey: ['account', 'addresses'] })
        } catch {
          /* ignore — the order succeeded; the saved address is a convenience */
        }
      }
      clear()
      // Straight to the live track view; the order is also under /account.
      navigate(`/track/${order.trackingToken}`, { replace: true })
    } catch (err) {
      // Show the server's own reason ("Delivery address is required…", "Product not available: X")
      // — a customer can act on that, where the generic fallback only invited a blind retry. The
      // fallback still covers a network drop, which has no body. Cart stays intact for a retry.
      setError(errorMessage(err, t('checkout.error')))
      setSubmitting(false)
    }
  }

  return (
    <Container maxWidth="sm" sx={{ py: 2, pb: 6 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        {t('checkout.title')}
      </Typography>

      <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'rgba(59,42,30,0.1)', borderRadius: '16px', boxShadow: '0 3px 14px rgba(59,42,30,0.16)' }}>
        <Stack spacing={1}>
        {items.map((item) => (
          <Stack key={item.key} direction="row" justifyContent="space-between">
            <Typography variant="body2" sx={{ mr: 1 }}>
              {item.product.name} × {item.quantity}
              {item.selections && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {Object.entries(item.selections).map(([g, o]) => `${g}: ${o}`).join('; ')}
                </Typography>
              )}
            </Typography>
            {/* Priceless shop (D-069): line quantity above already shows the count; no money here. */}
            {showPrices && item.product.price != null && (
              <Typography variant="body2">{formatMoney(item.product.price * item.quantity, currency)}</Typography>
            )}
          </Stack>
        ))}
        {showPrices && (
          <>
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle1" fontWeight={700}>
                {t('common.total')}
              </Typography>
              <Typography variant="subtitle1" fontWeight={700}>
                {formatMoney(subtotal, currency)}
              </Typography>
            </Stack>
          </>
        )}
        </Stack>
      </Box>

      <Stack spacing={2}>
        <ToggleButtonGroup
          value={fulfillment}
          exclusive
          fullWidth
          color="primary"
          onChange={(_e, value) => {
            if (value) {
              setFulfillment(value as FulfillmentType)
            }
          }}
          aria-label={t('checkout.fulfillmentMethod')}
        >
          <ToggleButton value="DELIVERY">{t('checkout.delivery')}</ToggleButton>
          <ToggleButton value="PICKUP">{t('checkout.pickup')}</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
          {isDelivery ? t('checkout.deliveryHint') : t('checkout.pickupHint')}
        </Typography>

        <TextField
          label={t('common.yourName')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={touched && !!nameError}
          helperText={(touched && nameError) || ' '}
          required
          fullWidth
          inputProps={{ maxLength: 255 }}
        />
        <TextField
          label={t('common.phoneNumber')}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          error={touched && !!phoneError}
          helperText={(touched && phoneError) || ' '}
          required
          fullWidth
          type="tel"
          inputProps={{ maxLength: 32 }}
        />
        {isDelivery && (
          <>
            {hasSaved && (
              <TextField
                select
                label={t('checkout.savedAddress')}
                value={selectedAddressId}
                onChange={(e) => onSelectAddress(e.target.value)}
                fullWidth
              >
                {addresses?.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.label}
                  </MenuItem>
                ))}
                <MenuItem value="new">{t('checkout.newAddressOption')}</MenuItem>
              </TextField>
            )}

            {isNewAddress ? (
              <>
                <TextField
                  label={t('checkout.deliveryAddress')}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  error={touched && addressError}
                  helperText={touched && addressError ? t('common.required') : ' '}
                  required
                  fullWidth
                  multiline
                  minRows={2}
                  inputProps={{ maxLength: 512 }}
                />
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={saveToAccount}
                        onChange={(e) => setSaveToAccount(e.target.checked)}
                      />
                    }
                    label={t('checkout.saveAddress')}
                  />
                  {saveToAccount && (
                    <TextField
                      label={t('checkout.addressLabel')}
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      error={touched && labelError}
                      helperText={touched && labelError ? t('common.required') : ' '}
                      fullWidth
                      inputProps={{ maxLength: 64 }}
                    />
                  )}
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ px: 0.5, whiteSpace: 'pre-wrap' }}>
                {address}
              </Typography>
            )}
          </>
        )}
        <TextField
          label={t('checkout.notesOptional')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          multiline
          minRows={1}
          inputProps={{ maxLength: 2000 }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={isDelivery ? t('checkout.cashOnDelivery') : t('checkout.cashOnPickup')}
            color="primary"
            variant="outlined"
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          />
          <Typography variant="body2" color="text.secondary">
            {isDelivery ? t('checkout.payOnDelivery') : t('checkout.payAtShop')}
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Button variant="contained" size="large" fullWidth disabled={submitting} onClick={handleSubmit} sx={{ borderRadius: '14px', py: 1.5 }}>
          {submitting
            ? t('checkout.placing')
            : showPrices
              ? t('checkout.placeOrder', { amount: formatMoney(subtotal, currency) })
              : t('checkout.placeOrderPlain')}
        </Button>
      </Stack>
    </Container>
  )
}
